import React, { useEffect, useState, useContext } from 'react';
import {
  Card, Form, Input, Button, List, Modal, Space, message, Typography
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined
} from '@ant-design/icons';
import { LoginContext } from '../../../../context/loginContext';

const { Title } = Typography;

const CDatosCliente = () => {
  const { user, login } = useContext(LoginContext);
  const [form] = Form.useForm();
  const [cliente, setCliente] = useState(null);
  const [direcciones, setDirecciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [direccionModal, setDireccionModal] = useState({ visible: false, data: null });

  const authHeader = {
    Authorization: `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    if (user?.usu_id) {
      fetchCliente();
    }
  }, []); // ✅ solo al montar

  const fetchCliente = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/cliente/mis-datos/${user.usu_id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCliente(data.cliente);
      setDirecciones(data.direcciones);
      form.setFieldsValue(data.cliente);

      // ✅ actualizar todo el objeto user en contexto, incluyendo direcciones
      login({
        ...user,
        direcciones: data.direcciones,
        cliente: data.cliente
      });
    } catch (err) {
      message.error('Error al obtener datos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/cliente/${cliente.cli_id}`, {
        method: 'PUT',
        headers: authHeader,
        body: JSON.stringify(values)
      });
      if (!res.ok) throw new Error();
      message.success('Datos actualizados');
      fetchCliente(); // también actualiza el contexto
    } catch {
      message.error('Error al actualizar datos');
    }
  };

  const handleGuardarDireccion = async (values) => {
    try {
      let res;
      if (direccionModal.data) {
        // Editar
        res = await fetch(`${process.env.REACT_APP_API_URL}/cliente/direcciones/${direccionModal.data.dci_id}`, {
          method: 'PUT',
          headers: authHeader,
          body: JSON.stringify(values)
        });
      } else {
        // Crear
        res = await fetch(`${process.env.REACT_APP_API_URL}/cliente/direcciones`, {
          method: 'POST',
          headers: authHeader,
          body: JSON.stringify({
            ...values,
            dci_cliente: cliente.cli_id
          })
        });
      }
      if (!res.ok) throw new Error();
      message.success(direccionModal.data ? 'Dirección actualizada' : 'Dirección agregada');
      setDireccionModal({ visible: false, data: null });
      fetchCliente(); // 🔁 actualiza también el contexto
    } catch {
      message.error('Error al guardar dirección');
    }
  };

  const handleEliminarDireccion = async (id) => {
    Modal.confirm({
      title: '¿Eliminar dirección?',
      onOk: async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/cliente/direcciones/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${user.token}` }
          });
          if (!res.ok) throw new Error();
          message.success('Dirección eliminada');
          fetchCliente(); // 🔁 actualiza también el contexto
        } catch {
          message.error('Error al eliminar dirección');
        }
      }
    });
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Title level={3}>Mis Datos</Title>
      <Card loading={loading}>
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item label="Email" name="cli_email">
            <Input readOnly style={{ backgroundColor: '#f5f5f5' }} />
          </Form.Item>
          <Form.Item label="Nombre" name="cli_nombre">
            <Input />
          </Form.Item>
          <Form.Item label="Teléfono" name="cli_telefono">
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Guardar</Button>
          </Form.Item>
        </Form>
      </Card>

      <Title level={4} style={{ marginTop: 32 }}>Direcciones</Title>
      <Button icon={<PlusOutlined />} type="dashed" onClick={() => setDireccionModal({ visible: true, data: null })}>
        Agregar dirección
      </Button>
      <List
        dataSource={direcciones}
        renderItem={dir => (
          <List.Item
            actions={[
              <Button icon={<EditOutlined />} onClick={() => setDireccionModal({ visible: true, data: dir })} />,
              // <Button danger icon={<DeleteOutlined />} onClick={() => handleEliminarDireccion(dir.dci_id)} />
            ]}
          >
            <List.Item.Meta
              title={dir.dci_direccion}
              description={`${dir.dci_localidad} - CP ${dir.dci_codigoposta}${dir.dci_observacion ? ` (${dir.dci_observacion})` : ''}`}
            />
          </List.Item>
        )}
      />

      <Modal
        open={direccionModal.visible}
        title={direccionModal.data ? 'Editar dirección' : 'Nueva dirección'}
        onCancel={() => setDireccionModal({ visible: false, data: null })}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          initialValues={direccionModal.data || {}}
          onFinish={handleGuardarDireccion}
        >
          <Form.Item label="Dirección" name="dci_direccion" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Localidad" name="dci_localidad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Código Postal" name="dci_codigoposta" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Observación" name="dci_observacion">
            <Input />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setDireccionModal({ visible: false, data: null })}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Guardar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CDatosCliente;